import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon, BookOpenIcon, ClockIcon, SparklesIcon, StarIcon } from 'lucide-react';
import { PageHeader } from '../components/page/PageHeader';
import { CtaBand } from '../components/page/CtaBand';
import { Reveal } from '../components/ui/Reveal';
import { AGENCY_CATEGORIES, RESOURCE_GUIDES } from '../components/resourcesData';
const SEO = {
  title: 'Influencer Talent Agency Resources & Guides | Zano',
  description: 'Discover recommended agencies by category with live creator rosters on Zano, learn how influencer talent agencies work and explore practical guides about running influencer campaigns with talent agencies.',
  keywords: 'influencer talent agencies, talent agencies, influencer agencies, creator representation, influencer rosters, talent managers, represented creators',
  ogTitle: 'Recommended Talent Agencies, Resources & Guides | Zano',
  ogDescription: 'Browse recommended agencies by category, learn how talent agencies work and explore practical guides for running campaigns with talent agencies.'
};
type TabId = 'agencies' | 'guides';
const TABS: {
  id: TabId;
  label: string;
  helper: string;
}[] = [{
  id: 'agencies',
  label: 'Recommended agencies',
  helper: "Editor's picks by category"
}, {
  id: 'guides',
  label: 'Guides',
  helper: 'How the market works'
}];
function updateMetaTag(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const existingTag = document.head.querySelector<HTMLMetaElement>(selector);
  const tag = existingTag ?? document.createElement('meta');
  const previousContent = existingTag?.getAttribute('content') ?? null;
  if (!existingTag) {
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
  return () => {
    if (!existingTag) {
      tag.remove();
      return;
    }
    if (previousContent === null) {
      existingTag.removeAttribute('content');
    } else {
      existingTag.setAttribute('content', previousContent);
    }
  };
}
function getInitials(name: string) {
  return name.replace(/[^a-zA-Z\s&]/g, ' ').split(/\s+/).filter(word => word.length > 0 && word !== '&').slice(0, 2).map(word => word[0].toUpperCase()).join('');
}
function AgenciesPanel() {
  return <div id="panel-agencies" role="tabpanel" aria-labelledby="tab-agencies" data-id="element-2779" data-mp="3q69f1sy8ency">
      <Reveal data-id="element-2780" data-mp="3q8c9cp6f32nh">
        <div className="w-full" data-id="element-2781" data-mp="3u7b9lwh7eb8l">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-zano-pink/25 bg-zano-blush px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-zano-pink" data-id="element-2782" data-mp="3u69vtk8szan6">
            <StarIcon size={12} aria-hidden="true" data-id="element-2783" data-mp="7zblwl1p6348" /> Curated by the Zano team
          </span>
          <h2 className="mt-4 text-2xl font-bold tracking-tight text-zano-charcoal sm:text-3xl" data-id="element-2784" data-mp="24tg36tzqzern">Best Influencer Talent Agencies by Category</h2>
          <p className="mt-3 text-base leading-relaxed text-gray-600" data-id="element-2785" data-mp="29tu47s2xlog6">The influencer marketing industry is home to thousands of talent agencies, each specializing in different creator niches and campaign types. We've curated this list of some of the best influencer talent agencies across categories including gaming, family, AI, sports, celebrities, fashion, and more.</p>
          <p className="mt-3 text-sm leading-relaxed text-gray-500" data-id="element-2786" data-mp="1nkna5svpfi6o">Every agency featured below is listed on Zano, giving you access to their live roster and real-time creator data including follower count, average views, engagement rate as well as agency-managed details like rates by deliverable, collaboration types and direct talent manager contact information.</p>
        </div>
      </Reveal>

      <div className="mt-9 grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-id="element-2787" data-mp="2690q0q0nu4fm">
        {AGENCY_CATEGORIES.map((entry, index) => <Reveal key={entry.category} delay={Math.min(index, 5) * 0.06} className="h-full" data-id="element-2788" data-mp="zrnbc4y1z7ux" data-mpi={index}>
            <article className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm" data-id="element-2789" data-mp="utc2nxjhv3ea">
              <h3 className="text-base font-bold tracking-tight text-zano-charcoal" data-id="element-2790" data-mp="2jmzv04we0edb">{entry.category}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600" data-id="element-2791" data-mp="2ohdx0q5k1jqy">{entry.description}</p>

              <ul className="mt-5 space-y-1.5 border-t border-gray-100 pt-4" data-id="element-2792" data-mp="29ott3nakz88g">
                {entry.agencies.map(agency => <li key={agency.name} data-id="element-2793" data-mp="32esqc5yxibo7">
                    <a href={agency.rosterUrl} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 rounded-lg px-2.5 py-2 transition-colors hover:bg-zano-blush/60" data-id="element-2794" data-mp="32fvh8dw94e44">
                      {agency.logoUrl ? <img src={agency.logoUrl} alt={`${agency.name} logo`} className="h-8 w-8 shrink-0 rounded-md border border-gray-200 bg-white object-contain p-0.5" data-id="element-2795" data-mp="1h296f051vkb8" /> : <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-[10px] font-bold text-gray-500" data-id="element-2796" data-mp="3va9zb6quku3n">
                          {getInitials(agency.name)}
                        </span>}
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-zano-charcoal transition-colors group-hover:text-zano-pink" data-id="element-2797" data-mp="3cj93grl0xeob">{agency.name}</span>
                      <span className="inline-flex shrink-0 items-center gap-1 text-xs font-bold text-zano-pink" data-id="element-2798" data-mp="3qbyp2uwn4hkw">
                        View live roster
                        <ArrowUpRightIcon size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" data-id="element-2799" data-mp="2xtuv9mt6p3ai" />
                      </span>
                    </a>
                  </li>)}
              </ul>
            </article>
          </Reveal>)}
      </div>
    </div>;
}
function GuidesPanel() {
  return <div id="panel-guides" role="tabpanel" aria-labelledby="tab-guides" data-id="element-2800" data-mp="16i856wc0eplz">
      <Reveal data-id="element-2801" data-mp="16k19i4z5gxqt">
        <div className="max-w-3xl" data-id="element-2802" data-mp="1bh1gci3tq4iz">
          <h2 className="text-2xl font-bold tracking-tight text-zano-charcoal sm:text-3xl" data-id="element-2803" data-mp="1bfzvxn0x45ol">Guides to working with talent agencies</h2>
          <p className="mt-3 text-base leading-relaxed text-gray-600" data-id="element-2804" data-mp="dv3wohdpjt5g">Plain-language explanations of how representation, rosters, pricing, and campaign workflows actually work — so you can approach agencies with confidence.</p>
        </div>
      </Reveal>

      <div className="mt-9 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-2805" data-mp="sqtj2gmt28ap">
        {RESOURCE_GUIDES.map((guide, index) => <Reveal key={guide.slug} delay={Math.min(index, 5) * 0.06} className="h-full" data-id="element-2806" data-mp="1z5u965p5fw9d" data-mpi={index}>
            <Link to={`/resources/${guide.slug}`} className="group flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-zano-pink/40 hover:shadow-float" data-id="element-2807" data-mp="244nhljpkwx2o">
              <div className="flex items-center justify-between" data-id="element-2808" data-mp="3ex64mdnbtlxs">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-zano-blush text-zano-pink" data-id="element-2809" data-mp="39yux49ki7niq"><BookOpenIcon size={19} aria-hidden="true" data-id="element-2810" data-mp="34zs11emi4rmw" /></span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500" data-id="element-2811" data-mp="35134tefy5ze0"><ClockIcon size={13} aria-hidden="true" data-id="element-2812" data-mp="30xdlb3e4q7zu" />{guide.readingTime}</span>
              </div>
              <h3 className="mt-5 text-lg font-bold leading-snug tracking-tight text-zano-charcoal transition-colors group-hover:text-zano-pink" data-id="element-2813" data-mp="2vzls861r0fu2">{guide.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-gray-600" data-id="element-2814" data-mp="u88nbgzs67y0">{guide.summary}</p>
              <div className="mt-5 flex flex-wrap gap-2" data-id="element-2815" data-mp="p848dveitl9w">
                {guide.topics.map(topic => <span key={topic} className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[10px] font-semibold text-gray-600" data-id="element-2816" data-mp="3u8e5vru0em28">{topic}</span>)}
              </div>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-zano-pink" data-id="element-2817" data-mp="3kjmky3nd920t">
                Read guide <ArrowUpRightIcon size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" data-id="element-2818" data-mp="3pgw7nbjdezky" />
              </span>
            </Link>
          </Reveal>)}
      </div>
    </div>;
}
export function Resources() {
  const [activeTab, setActiveTab] = useState<TabId>('agencies');
  const tabBarRef = useRef<HTMLDivElement>(null);
  const handleTabChange = (tabId: TabId) => {
    if (tabId === activeTab) return;
    setActiveTab(tabId);
    const tabBar = tabBarRef.current;
    if (!tabBar) return;

    // Keep the tab bar in view instead of letting the shorter panel
    // leave the viewport scrolled past the content.
    const offset = tabBar.getBoundingClientRect().top + window.scrollY - 64;
    window.scrollTo({
      top: offset,
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;
    const restoreDescription = updateMetaTag('name', 'description', SEO.description);
    const restoreKeywords = updateMetaTag('name', 'keywords', SEO.keywords);
    const restoreOgTitle = updateMetaTag('property', 'og:title', SEO.ogTitle);
    const restoreOgDescription = updateMetaTag('property', 'og:description', SEO.ogDescription);
    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreKeywords();
      restoreOgTitle();
      restoreOgDescription();
    };
  }, []);
  return <>
      <PageHeader eyebrow="Resources" eyebrowClass="bg-zano-blush text-zano-pink border border-zano-pink/20" title="Everything You Need to Know About Working with Talent Agencies" subtitle="Discover recommended agencies by category, learn how influencer talent agencies work and explore practical guides about running influencer campaigns with agencies" data-id="element-2819" data-mp="3cxiqcgo0veha" />

      <div ref={tabBarRef} className="sticky top-16 z-30 border-y border-gray-100 bg-white/95 backdrop-blur-md" data-id="element-2820" data-mp="3fpm3wunmpexl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-2821" data-mp="36mut8x2kun70">
          <div role="tablist" aria-label="Resource sections" className="flex gap-1 overflow-x-auto py-2.5" data-id="element-2822" data-mp="36onzwcd6x7f0">
            {TABS.map(tab => {
            const isActive = tab.id === activeTab;
            return <button key={tab.id} id={`tab-${tab.id}`} role="tab" type="button" aria-selected={isActive} aria-controls={`panel-${tab.id}`} onClick={() => handleTabChange(tab.id)} className={`flex shrink-0 items-center gap-2.5 rounded-xl px-4 py-2.5 text-left transition-colors ${isActive ? 'bg-zano-blush text-zano-pink' : 'text-gray-600 hover:bg-gray-50 hover:text-zano-charcoal'}`} data-id="element-2823" data-mp="2azb2qpqyihy">
                  {tab.id === 'agencies' ? <StarIcon size={17} aria-hidden="true" data-id="element-2824" data-mp="28tvdock461mq" /> : <BookOpenIcon size={17} aria-hidden="true" data-id="element-2825" data-mp="2cv6y83o4rwo2" />}
                  <span data-id="element-2826" data-mp="2ctm60kpagptd">
                    <span className="block text-sm font-semibold" data-id="element-2827" data-mp="2hu0e22qw3zre">{tab.label}</span>
                    <span className={`block text-xs ${isActive ? 'text-zano-pink/80' : 'text-gray-500'}`} data-id="element-2828" data-mp="2hrnl6ujlg4zk">{tab.helper}</span>
                  </span>
                </button>;
          })}
          </div>
        </div>
      </div>

      <main className="py-16 sm:py-20" data-id="element-2829" data-mp="2wio3rse21f9f">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-2830" data-mp="2wjprawc9bu81">
          {activeTab === 'agencies' ? <AgenciesPanel data-id="element-2831" data-mp="30jzu604r64io" /> : <GuidesPanel data-id="element-2832" data-mp="30mm76faexc8a" />}
        </div>
      </main>

      <section className="border-t border-gray-100 bg-gray-50/60 py-14" aria-labelledby="resources-next-title" data-id="element-2833" data-mp="35knnisggexg2">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8" data-id="element-2834" data-mp="3ajh2kpbe2wic">
          <SparklesIcon size={24} className="mx-auto text-zano-pink" aria-hidden="true" data-id="element-2835" data-mp="1afobx9yvygx3" />
          <h2 id="resources-next-title" className="mt-4 text-xl font-bold tracking-tight text-zano-charcoal sm:text-2xl" data-id="element-2836" data-mp="15hv3096778t4">We’re adding new guides regularly</h2>
          <p className="mt-3 text-sm leading-relaxed text-gray-600 sm:text-base" data-id="element-2837" data-mp="1k8c6dy2e88sn">This hub grows around the questions brands and marketing agencies ask most when they start working with represented creators.</p>
        </div>
      </section>

      <CtaBand title="Put what you learn into practice" subtitle="Search live talent agency rosters and start working with represented creators directly." primaryCta={{
      label: 'Start Free Trial',
      to: '/pricing'
    }} data-id="element-2838" data-mp="1p5vjguncufj0" />
    </>;
}